import React, { useMemo, useState, useCallback } from "react";
import Select from "react-select";
import cs from "classnames";
import debounce from "lodash/debounce";

import { useDirectorySearch } from "../../../../lib/hooks/useDirectorySearch";
import { useBookingsMapContext } from "../../BookingsMap/Context/BookingsMapContext";

import { Person } from "../../../../api/grpc/contacts/contacts";
import { UserModal } from "../../shared/UserModal/UserModal";
import { SearchUserCustomOption } from "../../../shared/Forms/SearchUserInput/SearchUserCustomOption";
import { DirectoryMenuList } from "../../../shared/DirectoryMenuList/DirectoryMenuList";
import { CustomNoOptionsMessage } from "../../../shared/NoOptionsMessage/NoOptionsMessage";

interface Props {
  bookOnBehalf?: boolean;
}

export const SelectPeopleList = ({ bookOnBehalf }: Props) => {
  const [selectedUser, setSelectedUser] = useState<Person | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { setBookOnBehalfUser } = useBookingsMapContext();

  const {
    searching,
    searchError,
    searchResults,
    setSearchResults,
    handleDirectorySearch,
    handleIntegrationSearch,
  } = useDirectorySearch();

  // Implement debounced search function
  const debouncedSearch = useCallback(
    debounce((query: string) => {
      if (query && query.length > 1) {
        handleDirectorySearch(query);
      }
    }, 500),
    [handleDirectorySearch]
  );

  const handleInputChange = (inputValue: string) => {
    if (inputValue && inputValue.length > 1) {
      debouncedSearch(inputValue);
    }
  };

  const handleManualDirectorySearch = (query: string) => {
    if (query) {
      handleIntegrationSearch(query);
    }
  };

  const handleSelectChange = (person: Person | null) => {
    if (!person) {
      setSelectedUser(null);
      return;
    }

    setSelectedUser(person);

    if (bookOnBehalf) {
      setBookOnBehalfUser(person);
    } else {
      setIsOpen(true);
    }
  };

  const options = useMemo(() => {
    if (!searchResults?.people?.length) return [];

    return searchResults.people.map((item) => ({
      value: item,
      label: item.displayName,
    }));
  }, [searchResults]);

  const selectedValue = useMemo(() => {
    return selectedUser
      ? { label: selectedUser.displayName, value: selectedUser }
      : null;
  }, [selectedUser]);

  return (
    <>
      {searchError ? (
        <div>Error fetching people, please try again.</div>
      ) : (
        <Select
          isClearable
          options={options}
          placeholder={bookOnBehalf ? "Search user..." : "Find people"}
          onChange={(e) => handleSelectChange(e?.value || null)}
          onInputChange={handleInputChange}
          value={selectedValue}
          noOptionsMessage={() => "No result found"}
          onMenuClose={() => {
            if (searchResults?.people?.length) {
              setSearchResults(undefined);
            }
          }}
          components={{
            MenuList: (props) => (
              <DirectoryMenuList
                {...props}
                searching={searching}
                handleDirectorySearch={handleManualDirectorySearch}
                showNarrowOption={
                  searchResults?.people && searchResults?.people.length > 8
                }
              />
            ),
            Option: SearchUserCustomOption as any,
            NoOptionsMessage: (props) => (
              <CustomNoOptionsMessage {...props} hideMessage={!!searching} />
            ),
          }}
          className={cs(
            "FindPeople FindPeopleSearchDesk PeopleSelect no-min-height NoOption mb-3"
          )}
          classNamePrefix="FindPeopleSelect"
        />
      )}

      {isOpen && (
        <UserModal
          person={selectedUser}
          isOpen={isOpen}
          handleModalClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};
